import { useEffect, useState } from "react";
import { api, type InclusionProof } from "../api/client";
import { verifyInclusion } from "../crypto/merkle";

/** The claim's rail entry on Polygon: pending until the hourly rail batch, then
 *  the block, the inclusion proof (recomputed here) and an explorer link. */
export function PolygonLine({ entryHash, poll }: { entryHash?: string; poll?: number }) {
  const [p, setP] = useState<InclusionProof | null>(null);
  useEffect(() => {
    if (!entryHash) return;
    let live = true;
    const load = () => api.GET("/rail/entries/{entry_hash}/inclusion", { params: { path: { entry_hash: entryHash } } })
      .then((r) => { if (live && r.data) setP(r.data); });
    load();
    const t = poll ? setInterval(load, poll) : undefined;
    return () => { live = false; if (t) clearInterval(t); };
  }, [entryHash, poll]);
  if (!p) return null;
  if (p.status === "pending")
    return <div className="polygon" data-testid="polygon" data-status={p.status}>
      Polygon: waiting for the next rail batch{p.next_batch_at ? ` (${new Date(p.next_batch_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})` : ""}.
    </div>;
  if (p.status === "batched")
    return <div className="polygon" data-testid="polygon" data-status="batched">
      <div>In rail batch {p.batch_id}; inclusion proof {verifyInclusion(p) ? "recomputes to the batch root" : "does NOT recompute to the batch root"} ({p.proof?.length ?? 0} steps, {p.hash_algorithm}).</div>
      <div>Not yet published on Polygon; it goes out with the next run.</div>
    </div>;
  const recomputes = verifyInclusion(p);
  return (
    <div className="polygon" data-testid="polygon" data-status="published">
      <div>Polygon{p.is_testnet ? " (testnet)" : ""} · block <b data-testid="block">{p.block_number?.toLocaleString()}</b> · batch {p.batch_id}</div>
      <div>Inclusion proof {recomputes ? "recomputes to the published root" : "does NOT recompute to the published root"} ({p.proof?.length ?? 0} steps, {p.hash_algorithm}).</div>
      {p.explorer_link && <div><a href={p.explorer_link} target="_blank" rel="noreferrer">View the transaction on the explorer</a></div>}
    </div>
  );
}
